// src/components/DietLog.jsx
import { useState, useEffect, useMemo } from "react";
import { 
  getAllDietItems, addDietItem, saveDietEntry, 
  subscribeDietLogs, deleteDietEntry 
} from "../firebase/database";
import { todayStr } from "../utils/helpers";
import { theme } from "../utils/theme";

const C = theme.dark;

const MEAL_TIMES = ["Early", "Morning", "Noon", "Evening", "Night"];

export default function DietLog({ studentId, studentName, dailyKcalTarget, date: activeDate }) {
  const [dietItems, setDietItems] = useState([]);
  const [dietLogs, setDietLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form State
  const [form, setForm] = useState({ 
    foodItemId: "", foodName: "", qty: 1, mealTime: "Morning", 
    kcalPerUnit: 0, protein: 0, carbs: 0, fats: 0 
  });
  
  // Quick Add Item Modal
  const [showItemModal, setShowItemModal] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", kcal: "", protein: "", carbs: "", fats: "" });

  useEffect(() => {
    if (!studentId) return;
    const unsubLogs = subscribeDietLogs(studentId, setDietLogs);
    getAllDietItems().then(setDietItems);
    setLoading(false);
    return () => unsubLogs();
  }, [studentId]);

  const todayDiet = useMemo(() => 
    dietLogs.filter(l => l.date === activeDate), 
  [dietLogs, activeDate]);

  const pastDiet = useMemo(() => 
    dietLogs.filter(l => l.date !== activeDate), 
  [dietLogs, activeDate]);

  const totalConsumed = useMemo(() => 
    todayDiet.reduce((sum, log) => sum + (log.kcalPerUnit * log.qty), 0), 
  [todayDiet]);

  const handleItemSelect = (e) => {
    const id = e.target.value;
    const item = dietItems.find(i => i.id === id);
    if (item) {
      setForm({
        ...form,
        foodItemId: id,
        foodName: item.name,
        kcalPerUnit: parseFloat(item.kcal) || 0,
        protein: parseFloat(item.protein) || 0,
        carbs: parseFloat(item.carbs) || 0,
        fats: parseFloat(item.fats) || 0
      });
    }
  };

  const handleSaveEntry = async () => {
    if (!form.foodName || !form.qty) return;
    setSaving(true);
    try {
      await saveDietEntry(studentId, { ...form, entryId: editingId, date: activeDate });
      setForm({ foodItemId: "", foodName: "", kcalPerUnit: 0, qty: 1, mealTime: "Morning", protein: 0, carbs: 0, fats: 0 });
      setEditingId(null);
    } catch (err) {
      alert("Error saving diet entry");
    }
    setSaving(false);
  };

  const handleEdit = (log) => {
    setEditingId(log.entryId);
    setForm({
      foodItemId: log.foodItemId || "",
      foodName: log.foodName,
      qty: log.qty,
      mealTime: log.mealTime,
      kcalPerUnit: log.kcalPerUnit,
      protein: log.protein || 0,
      carbs: log.carbs || 0,
      fats: log.fats || 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuickAddItem = async () => {
    if (!newItem.name || !newItem.kcal) return;
    try {
      const id = await addDietItem(newItem);
      const updated = await getAllDietItems();
      setDietItems(updated);
      setForm({ 
        ...form, 
        foodItemId: id, 
        foodName: newItem.name, 
        kcalPerUnit: parseFloat(newItem.kcal) 
      });
      setShowItemModal(false);
      setNewItem({ name: "", kcal: "", protein: "", carbs: "", fats: "" });
    } catch (err) {
      alert("Failed to add food item");
    }
  };

  const handleDelete = async (entryId) => {
    if (window.confirm("Delete this log?")) {
      await deleteDietEntry(studentId, entryId);
    }
  };

  const target = dailyKcalTarget || 2000;
  const progressPct = Math.min(100, Math.round((totalConsumed / target) * 100));

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif", color: C.text }}>
      <style>{`
        .diet-card { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
        .form-group { margin-bottom: 16px; }
        .form-label { display: block; font-size: 12px; color: ${C.muted}; margin-bottom: 6px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .form-input { width: 100%; padding: 10px 14px; border-radius: 8px; border: 1px solid ${C.border}; background: ${C.surface}; color: ${C.text}; outline: none; }
        .btn-primary { background: ${C.accent}; color: white; border: none; padding: 12px; border-radius: 8px; cursor: pointer; font-weight: 700; width: 100%; }
        .modal { position: fixed; inset: 0; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: center; z-index: 2000; }
        .modal-content { background: ${C.card}; padding: 32px; border-radius: 16px; width: 400px; }
      `}</style>

      {/* Quick Add Food Modal */}
      {showItemModal && (
        <div className="modal">
          <div className="modal-content">
            <h3 style={{ marginBottom: 20 }}>Add New Food Item</h3>
            <div className="form-group">
              <label className="form-label">Food Name</label>
              <input className="form-input" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} placeholder="e.g. Paneer Tikka" />
            </div>
            <div className="form-group">
              <label className="form-label">Kcal per unit/serving</label>
              <input type="number" className="form-input" value={newItem.kcal} onChange={e => setNewItem({...newItem, kcal: e.target.value})} placeholder="0" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Prot (g)</label>
                <input type="number" className="form-input" value={newItem.protein} onChange={e => setNewItem({...newItem, protein: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Carb (g)</label>
                <input type="number" className="form-input" value={newItem.carbs} onChange={e => setNewItem({...newItem, carbs: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Fat (g)</label>
                <input type="number" className="form-input" value={newItem.fats} onChange={e => setNewItem({...newItem, fats: e.target.value})} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <button onClick={() => setShowItemModal(false)} style={{ flex: 1, padding: 12, borderRadius: 8, border: `1px solid ${C.border}`, background: "none", color: C.muted }}>Cancel</button>
              <button onClick={handleQuickAddItem} className="btn-primary" style={{ flex: 1 }}>Add Item</button>
            </div>
          </div>
        </div>
      )}

      {/* Kcal Progress Bar */}
      <div className="diet-card" style={{ background: C.surface }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: C.muted, fontWeight: 700 }}>CALORIE INTAKE ({activeDate})</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: totalConsumed > target ? C.red : C.green }}>
              {totalConsumed} / {target} <span style={{ fontSize: 14, color: C.muted, fontWeight: 400 }}>kcal</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, color: C.muted, fontWeight: 700 }}>REMAINING</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{Math.max(0, target - totalConsumed)} kcal</div>
          </div>
        </div>
        <div style={{ height: 12, background: C.dim, borderRadius: 6, overflow: "hidden" }}>
          <div style={{ 
            width: `${progressPct}%`, 
            height: "100%", 
            background: totalConsumed > target ? C.red : C.accent, 
            transition: "width 0.5s ease" 
          }} />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Entry Form */}
        <div className="diet-card" style={{ border: editingId ? `1px solid ${C.accent}` : `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
             <h4 style={{ margin: 0 }}>{editingId ? "Edit Meal Entry" : "Log Your Meal"}</h4>
             {editingId && (
               <button onClick={() => { setEditingId(null); setForm({ foodItemId: "", foodName: "", kcalPerUnit: 0, qty: 1, mealTime: "Morning", protein: 0, carbs: 0, fats: 0 }); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13 }}>Cancel Edit</button>
             )}
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Food Item</span>
              <button onClick={() => setShowItemModal(true)} style={{ background: "none", border: "none", color: C.accent, fontSize: 11, cursor: "pointer" }}>+ Add New Food</button>
            </label>
            <select className="form-input" value={form.foodItemId} onChange={handleItemSelect}>
              <option value="">Select an item...</option>
              {dietItems.map(item => (
                <option key={item.id} value={item.id}>{item.name} ({item.kcal} kcal)</option>
              ))}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input type="number" className="form-input" value={form.qty} onChange={e => setForm({...form, qty: parseFloat(e.target.value) || 1})} />
            </div>
            <div className="form-group">
              <label className="form-label">Meal Time</label>
              <select className="form-input" value={form.mealTime} onChange={e => setForm({...form, mealTime: e.target.value})}>
                {MEAL_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {form.kcalPerUnit > 0 && (
            <div style={{ padding: 12, background: C.surface, borderRadius: 8, marginBottom: 16, textAlign: "center", border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.muted }}>ESTIMATED KCAL</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>{Math.round(form.kcalPerUnit * form.qty)} kcal</div>
            </div>
          )}
          <button onClick={handleSaveEntry} disabled={saving || !form.foodName} className="btn-primary">
            {saving ? "Logging..." : editingId ? "Update Meal Entry ✓" : "Log Meal Entry ✓"}
          </button>
        </div>

        {/* Today's List */}
        <div className="diet-card">
          <h4 style={{ marginBottom: 16 }}>Logs for {activeDate} ({todayDiet.length})</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {todayDiet.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: C.muted }}>No meals logged yet today.</div>
            ) : (
              todayDiet.map(log => (
                <div key={log.entryId} style={{ display: "flex", justifyContent: "space-between", padding: "16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{log.foodName}</div>
                    <div style={{ fontSize: 13, color: C.muted }}>{log.mealTime} · Qty: {log.qty} · {Math.round(log.kcalPerUnit * log.qty)} kcal</div>
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <button onClick={() => handleEdit(log)} style={{ background: "none", border: "none", color: C.accent, fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Edit</button>
                    <button onClick={() => handleDelete(log.entryId)} style={{ background: "none", border: "none", color: C.red, fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* History Togle */}
        {pastDiet.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{ width: "100%", padding: 12, background: "none", border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted, fontSize: 13, cursor: "pointer" }}
            >
              {showHistory ? "Hide Past Logs" : `Show Past Logs (${pastDiet.length})`}
            </button>
            {showHistory && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                {pastDiet.map(log => (
                  <div key={log.entryId} style={{ display: "flex", justifyContent: "space-between", padding: "14px", background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, opacity: 0.8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{log.foodName} <span style={{ fontWeight: 400, color: C.muted, fontSize: 12 }}>({log.date})</span></div>
                      <div style={{ fontSize: 12, color: C.muted }}>{log.mealTime} · Qty: {log.qty} · {Math.round(log.kcalPerUnit * log.qty)} kcal</div>
                    </div>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <button onClick={() => handleEdit(log)} style={{ background: "none", border: "none", color: C.accent, fontSize: 12, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleDelete(log.entryId)} style={{ background: "none", border: "none", color: C.red, fontSize: 12, cursor: "pointer" }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
